import { useState, useEffect, useRef } from 'react';
import { Copy, Check, Loader2, AlertCircle, X } from 'lucide-react';
import { Level } from '../../types/world';
import { Button } from '../common/Button';
import { InlineCodeText } from '../common/InlineCodeText';
import { fetchWorldSolutions, LevelSolution } from '../../services/solutionService';
import { LecturerAuthenticationError } from '../../services/lecturerAuth';
import { useLecturerAuth } from '../../context/LecturerAuthContext';
import toast from 'react-hot-toast';

interface SolutionViewerProps {
  level: Level;
  worldId: string;
  onClose: () => void;
}

export function SolutionViewer({ level, worldId, onClose }: SolutionViewerProps) {
  const { credentials, logout, requestLogin } = useLecturerAuth();
  const [solution, setSolution] = useState<LevelSolution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authenticationFailed, setAuthenticationFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  useEffect(() => {
    if (!credentials) return;
    const controller = new AbortController();
    setSolution(null);
    setLoading(true);
    setError(null);
    setAuthenticationFailed(false);
    fetchWorldSolutions(worldId, credentials, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return;
        const current = data?.solutions.find(item => item.levelId === level.id);
        if (!current?.solution) {
          setError('Solution not found for this level.');
        } else {
          setSolution(current);
        }
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        setAuthenticationFailed(error instanceof LecturerAuthenticationError);
        setError(error instanceof LecturerAuthenticationError
          ? 'Your login is no longer valid. Please sign in again.'
          : 'Unable to load the solution. Please try again.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [worldId, level.id, credentials, attempt]);

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
      toast.success('Solution copied to clipboard!');
    } catch {
      toast.error('Failed to copy solution');
    }
  };

  return (
    <aside id="level-solution" aria-label="Level solution" className="min-w-0 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-lg flex flex-col self-start">
      <div className="flex items-start justify-between gap-3 p-4 border-b border-yellow-200 dark:border-yellow-800">
        <div className="min-w-0">
          <h3 className="font-semibold text-yellow-900 dark:text-yellow-100">Lecturer Solution</h3>
          <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-1"><InlineCodeText>{level.name}</InlineCodeText></p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button onClick={() => solution && handleCopy(solution.solution)} disabled={!solution || loading || !!error} variant="secondary" size="sm" className="flex items-center gap-1">
            {copied ? <Check size={16} /> : <Copy size={16} />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </Button>
          <button type="button" onClick={onClose} aria-label="Close solution" className="p-2 rounded-lg text-yellow-900 dark:text-yellow-100 hover:bg-yellow-100 dark:hover:bg-yellow-900 focus-visible:ring-2 focus-visible:ring-primary-500">
            <X size={20} />
          </button>
        </div>
      </div>
      <div className="p-4 overflow-auto max-h-[650px] min-h-[200px]">
        {loading && (
          <div role="status" className="flex items-center justify-center gap-2 py-8 text-yellow-900 dark:text-yellow-100">
            <Loader2 className="animate-spin" size={24} />
            <span>Loading solution...</span>
          </div>
        )}
        {error && (
          <div role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-900 dark:text-red-100 space-y-3">
            <p className="flex items-start gap-2"><AlertCircle size={20} className="flex-shrink-0" />{error}</p>
            <Button size="sm" variant="secondary" onClick={() => {
              if (authenticationFailed) {
                logout();
                requestLogin();
              } else {
                setAttempt(previous => previous + 1);
              }
            }}>{authenticationFailed ? 'Log in again' : 'Try again'}</Button>
          </div>
        )}
        {!loading && !error && solution && (
          <>
            <pre className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800 text-sm text-gray-900 dark:text-gray-100 whitespace-pre font-mono overflow-x-auto"><code>{solution.solution}</code></pre>
            {solution.alternativeSolutions?.map((code, index) => (
              <details key={index} className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-yellow-900 dark:text-yellow-100">Alternative solution {index + 1}</summary>
                <Button onClick={() => handleCopy(code)} variant="secondary" size="sm" className="my-2">Copy alternative</Button>
                <pre className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800 text-sm text-gray-900 dark:text-gray-100 whitespace-pre font-mono overflow-x-auto"><code>{code}</code></pre>
              </details>
            ))}
          </>
        )}
      </div>
    </aside>
  );
}
